/**
 * @fileoverview Lines of Code API Service
 * @description API calls for the Lines of Code report feature
 * @module features/LinesOfCode/services/linesOfCodeApi
 */

import { apiClient } from '@/services';

const BASE_PATH = '/v1/lines-of-code';

export const linesOfCodeApi = {
  /**
   * Health check
   */
  health: () => apiClient.get(`${BASE_PATH}/health`),

  /**
   * Search repositories by name query
   * @param {string} query - Search string
   * @returns {Promise<object>} { repositories, count }
   */
  searchRepositories: (query) =>
    apiClient.get(`${BASE_PATH}/repositories`, { search: query }),

  /**
   * Fetch ALL repositories in the org
   * @returns {Promise<object>} { repositories, count }
   */
  fetchAllRepositories: () =>
    apiClient.get(`${BASE_PATH}/repositories/all`),

  /**
   * Submit a new LOC report job
   * @param {object} params
   * @param {Array<{org: string, repo: string}>} params.repositories
   * @param {number} [params.days_back=30]
   * @returns {Promise<object>} { job_id, status, cached }
   */
  submitJob: ({ repositories, days_back = 30 }) =>
    apiClient.post(`${BASE_PATH}/jobs`, { repositories, days_back }),

  /**
   * Get all jobs for current user
   * @returns {Promise<object>} { jobs, count }
   */
  fetchJobs: () => apiClient.get(`${BASE_PATH}/jobs`),

  /**
   * Get status of a specific job
   * @param {string} jobId
   * @returns {Promise<object>} Job status with progress
   */
  fetchJobStatus: (jobId) => apiClient.get(`${BASE_PATH}/jobs/${jobId}`),

  /**
   * Get report data (JSON) for a completed job
   * @param {string} jobId
   * @returns {Promise<object>} { data, generated_at }
   */
  fetchReport: (jobId) => apiClient.get(`${BASE_PATH}/reports/${jobId}`),

  /**
   * Get the download URL for an Excel report
   * @param {string} jobId
   * @returns {string} Full download URL
   */
  getDownloadUrl: (jobId) => {
    const baseUrl = apiClient.baseURL || '';
    return `${baseUrl}${BASE_PATH}/reports/${jobId}/download`;
  },

  /**
   * Get notifications for current user
   * @returns {Promise<object>} { notifications, unread_count }
   */
  fetchNotifications: () => apiClient.get(`${BASE_PATH}/notifications`),

  /**
   * Mark a notification as read
   * @param {number} notificationId
   * @returns {Promise<object>}
   */
  markNotificationRead: (notificationId) =>
    apiClient.put(`${BASE_PATH}/notifications/${notificationId}/read`),

  // ── Suites ──────────────────────────────────────────────
  fetchSuites: () => apiClient.get(`${BASE_PATH}/suites`),
  fetchSuite: (suiteId) => apiClient.get(`${BASE_PATH}/suites/${suiteId}`),
  createSuite: (data) => apiClient.post(`${BASE_PATH}/suites`, data),
  updateSuite: (suiteId, data) => apiClient.put(`${BASE_PATH}/suites/${suiteId}`, data),
  deleteSuite: (suiteId) => apiClient.delete(`${BASE_PATH}/suites/${suiteId}`),
};
